import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Opening } from 'api-clients/monolith/models/Opening';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { getOpenings } from '@/api-clients/get-openings';
import { Pagination } from '@/components/base/pagination';
import { Search } from '@/components/base/search';
import { DataTable } from '@/components/ui/data-table';
import { Typography } from '@/components/ui/typography';
import { useTableContext } from '@/contexts/table-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { useOpeningColumns } from './columns';
import { Filters } from './filters';
import { NoOpenings } from './no-openings';
import { RowsSelect } from './rows-select';

export function OpeningsTable() {
  const columns = useOpeningColumns();

  const {
    pagination: { pageIndex, pageSize },
    rowCount,
    setRowCount,
    searchQuery,
    setSearchQuery,
    filters: {
      locationIds,
      positionIds,
      isSelectedForSourcing,
      brandIds,
      active,
    },
    sorting,
  } = useTableContext();
  const sort = sorting[0];

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...queryKeys.allOpenings,
      pageIndex,
      searchQuery,
      locationIds,
      positionIds,
      isSelectedForSourcing,
      brandIds,
      active,
      sorting,
    ],
    queryFn: () =>
      getOpenings({
        page: pageIndex + 1,
        perPage: pageSize,
        locationIds,
        positionIds,
        isSelectedForSourcing,
        query: searchQuery,
        brandIds,
        active,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
      }),
  });

  if (isError) {
    throw new Error('Error fetching openings');
  }

  // invalidateQueries: callback invoked by RowsSelect on mutation success (cross-component)
  const refetchOpenings = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.allOpenings,
    });
  }, [queryClient]);

  const tableRows = useMemo(() => data?.openings ?? [], [data]);

  useEffect(() => {
    setRowCount(data?.pagination?.total_count ?? 0);
  }, [data, setRowCount]);

  const { table } = useDataTable<Opening>({
    columns,
    data: tableRows,
    rowCount,
    externalIdKey: 'funnel_external_id',
  });

  return (
    <>
      <Typography as="h2" variant="caption">
        <FormattedMessage
          defaultMessage="Openings"
          description="Table caption: count of rows is org job openings"
        />
      </Typography>
      <div className="flex justify-between gap-2">
        <Filters />
        <Search setSearchQuery={setSearchQuery} />
      </div>
      <DataTable
        table={table}
        columns={columns}
        isLoading={isLoading}
        emptyState={<NoOpenings />}
      />
      <Pagination table={table} />
      <RowsSelect table={table} refetchOpenings={refetchOpenings} />
    </>
  );
}
